/**
 * When a quote's visits cannot all be made, the approval does not happen
 * either — `docs/visit-page.md` §26.3, Board 17 G.
 *
 * The band sits directly under the quote's header, over a quote still *Sent*:
 * the headline, the sentence with the support code, and **Try again**, which
 * sends the same approve request. It is the server's answer that draws it —
 * the `409 approval_visits_failed` this page just met, or the latest refused
 * approval read back after a reload — so a refusal is never a toast that is
 * gone before anybody reads the code.
 */
import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@rowted/shared'
import { request } from '../lib/api.js'
import { VisitGlyph } from '../visit/glyphs.js'
import { BAND_BUTTON } from '../visit/band.js'
import { APPROVAL_REFUSED as WORDS } from './copy.js'

export interface ApprovalRefusal {
  supportCode: string
  /** The approve request to send again, or `null` where the customer pressed Accept. */
  request: unknown
}

/**
 * The office's approve, with the refusal it may meet. `approve` resolves
 * `true` when the quote was approved; a refusal draws the band and resolves
 * `false` rather than throwing, and any other error is the builder's own.
 */
export function useApprovalRefusal({ quoteId, status, onApproved, onError }: {
  quoteId: string
  status: string | undefined
  onApproved: () => Promise<void>
  onError: (message: string | null) => void
}) {
  const [refusal, setRefusal] = useState<ApprovalRefusal | null>(null)
  const [busy, setBusy] = useState(false)
  const out = status === 'sent' || status === 'viewed'

  useEffect(() => {
    if (!out) { setRefusal(null); return undefined }
    let live = true
    void request<{ refused: { supportCode: string; request: unknown } | null }>(
      `/quotes/${quoteId}/approval-refused`)
      .then((body) => {
        if (live) setRefusal(body.refused ? { supportCode: body.refused.supportCode, request: body.refused.request } : null)
      })
      .catch(() => { /* No band is the state the page already draws. */ })
    return () => { live = false }
  }, [quoteId, out])

  const approve = useCallback(async (body: unknown): Promise<boolean> => {
    setBusy(true)
    try {
      await request(`/quotes/${quoteId}/approve`, { method: 'POST', body })
      setRefusal(null)
      onError(null)
      await onApproved()
      return true
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'approval_visits_failed') {
        const code = (cause.details as { supportCode?: unknown } | undefined)?.supportCode
        setRefusal({ supportCode: typeof code === 'string' ? code : '', request: body })
        onError(null)
      } else {
        onError(cause instanceof Error ? cause.message : String(cause))
      }
      return false
    } finally {
      setBusy(false)
    }
  }, [quoteId, onApproved, onError])

  return { refusal: out ? refusal : null, approve, busy }
}

/** Board 17 G's band. **Try again** is drawn where there is a request to send again. */
export function ApprovalRefusedBand({ refusal, busy, onRetry }: {
  refusal: ApprovalRefusal
  busy: boolean
  onRetry: (request: unknown) => void
}) {
  return (
    <div
      role="alert"
      data-as-drawn="vp-17 G"
      className="flex items-start gap-2.5 bg-danger-bg px-4 py-3 text-[12.5px] leading-[normal] tabular-nums text-danger"
    >
      <span className="mt-px inline-flex">
        <VisitGlyph name="warn" size={16} stroke={2.2} />
      </span>
      <div className="flex flex-1 flex-col">
        <b>{WORDS.headline}</b>
        <span className="mt-[3px] text-text-body">{WORDS.body(refusal.supportCode)}</span>
        {refusal.request !== null && (
          <div className="mt-[9px] flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => onRetry(refusal.request)}
              className={BAND_BUTTON.primary}
            >
              <VisitGlyph name="retry" size={14.5} stroke={2} />
              {WORDS.retry}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
